import { languageDisplayLabel, LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import {
  ModifiedQuestion,
  QuestionType,
  Quiz,
  SurveyAnswer,
} from '@/client/quizzes';
import { useToast } from '@/hooks/useToast';
import surveySubmittedImage from '@/images/placeholders/survey-submitted.svg';
import hardcodedTranslations from '@/locales/hardcodedTranslations';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { getTranslatedContent } from '@/utils/helpers';
import {
  modifyCourseQuiz,
  sortAlphabeticallyWithDKFirst,
} from '@/utils/helpers/courses.helper';
import { isMobile } from '@/utils/helpers/ui.helper';
import { AxiosError } from 'axios';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressBar } from 'primereact/progressbar';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

const StyledContainer = styled(FlexContainer)`
  height: auto;
  width: 100%;
  padding: calc(2 * var(--default-padding));

  h3 {
    font-size: var(--heading-small-font-size);
    line-height: var(--heading-small-line-height);
    font-weight: 700;
    margin-bottom: var(--medium-padding);
  }

  p {
    text-align: center;
    line-height: var(--medium-line-height);
    font-weight: 400;
  }

  .start-survey-view {
    p {
      width: 45%;
    }
  }

  .questions-survey-view {
    max-width: 720px;
    align-self: center;
  }
`;

const StyledProgressHeader = styled(FlexContainer)`
  width: 100%;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: var(--big-padding);

  .survey-progress-text {
    min-width: max-content;
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
    margin-right: var(--default-padding);
  }
`;

const StyledProgressBar = styled(ProgressBar)`
  width: 100%;
  height: 4px;
  background: var(--gray-dark);

  .p-progressbar-value {
    background: var(--purple-text);
  }
`;

const StyledQuestionContainer = styled(FlexContainer)<{
  isMobile: boolean;
}>`
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  padding-bottom: var(--big-padding);
  border-bottom: 1px solid var(--gray-dark);
  margin-bottom: var(--default-padding);

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
`;

const StyledQuestionText = styled.div`
  font-size: var(--heading-small-font-size);
  line-height: var(--heading-small-line-height);
  font-weight: 500;
  margin-bottom: var(--default-padding);
`;

const StyledScale = styled.div`
  width: 110px;
  height: 68px;
  border: 1px solid var(--gray-dark);
  border-radius: var(--xsmall-border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: var(--white-main);

  &:hover {
    border-color: var(--purple-text);
    background-color: var(--purple-pale);
  }

  &.selected {
    border-color: var(--purple-text);
    background-color: var(--purple-pale);
  }
`;

const StyledTextarea = styled(InputTextarea)`
  height: 127px;
  width: 100%;
  background-color: var(--white-main);
  border: 1px solid var(--gray-dark);
`;

const StyledScaleLabels = styled(FlexContainer)`
  font-size: var(--medium-font-size);
  line-height: var(--medium-line-height);
  margin-top: var(--small-padding);
  width: 100%;
`;

type SurveyPreviewProps = {
  survey: Quiz | undefined;
  sentSurveyId?: string;
  token?: string;
  isSubmitted?: boolean;
  urlLanguage?: LanguagesEnum;
};

enum ViewMode {
  START = 'start',
  QUESTIONS = 'questions',
  COMPLETED = 'completed',
  ALREADY_SUBMITTED = 'already-submitted',
}

export const SurveyPreview: React.FC<SurveyPreviewProps> = ({
  sentSurveyId,
  survey,
  token,
  isSubmitted,
  urlLanguage,
}) => {
  const toast = useToast();
  const [language, setLanguage] = useState<LanguagesEnum>(
    urlLanguage && survey?.supportedLanguages?.includes(urlLanguage)
      ? urlLanguage
      : LanguagesEnum.EN,
  );
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [viewMode, setViewMode] = useState<ViewMode>(
    isSubmitted ? ViewMode.ALREADY_SUBMITTED : ViewMode.START,
  );
  const [answers, setAnswers] = useState<SurveyAnswer[]>([]);
  const shouldAutoAdvanceRef = useRef(false);

  const modifiedSurvey = useMemo(() => {
    if (!survey) return undefined;
    return modifyCourseQuiz(survey);
  }, [survey]);

  const questions = modifiedSurvey?.questions || [];
  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const isCurrentQuestionAnswered = answers.some(
    (answer) => answer.questionId === currentQuestion?.id,
  );
  const showForwardButton =
    isLastQuestion ||
    currentQuestion?.type === QuestionType.TEXT ||
    isCurrentQuestionAnswered;

  useEffect(() => {
    if (shouldAutoAdvanceRef.current) {
      shouldAutoAdvanceRef.current = false;
      handleNextQuestion();
    }
  }, [answers]);

  const answerScale = (value: number) => {
    setAnswers((state) => [
      ...state.filter((answer) => answer.questionId !== currentQuestion.id),
      { questionId: currentQuestion.id as string, answer: value },
    ]);

    if (isLastQuestion) {
      shouldAutoAdvanceRef.current = true;
      return;
    }

    setCurrentQuestionIndex(currentQuestionIndex + 1);
  };

  const giveFeedback = (value: string) => {
    setAnswers((state) => [
      ...state.filter((answer) => answer.questionId !== currentQuestion.id),
      { questionId: currentQuestion.id as string, answer: value },
    ]);
  };

  const isSelectedScale = (scaleValue: number) => {
    return answers.find(
      (answer) =>
        answer.questionId === currentQuestion.id &&
        answer.answer === scaleValue,
    );
  };

  const handleNextQuestion = async () => {
    if (
      !answers.find(
        (answer) =>
          answer.questionId === currentQuestion.id ||
          currentQuestion.type === QuestionType.TEXT,
      )
    ) {
      toast?.error(
        hardcodedTranslations.survey.errorTitle[language],
        hardcodedTranslations.survey.errorAnswerRequired[language],
      );
      return;
    }

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else if (!sentSurveyId) {
      setViewMode(ViewMode.COMPLETED);
      return;
    } else {
      try {
        const response = await client.quizzes.submitSurveyForUser(
          {
            answers,
          },
          sentSurveyId,
          token,
        );

        if (
          (response as any)?.message ===
          'Your answers have been successfully submitted.'
        ) {
          setViewMode(ViewMode.COMPLETED);
        }
      } catch (e) {
        handleAxiosError(e as AxiosError, toast);
      }
    }
  };

  const handleBackToStart = () => {
    setViewMode(ViewMode.START);
    setCurrentQuestionIndex(0);
  };

  const renderScaleQuestion = (question: ModifiedQuestion) => {
    const questionText = getTranslatedContent(question, language, 'text');
    const scaleMinLabel = getTranslatedContent(
      question,
      language,
      'scaleMinLabel',
    );
    const scaleMaxLabel = getTranslatedContent(
      question,
      language,
      'scaleMaxLabel',
    );
    const scaleMin = question.scaleMin || 0;
    const scaleMax = question.scaleMax || 0;
    const scaleRange = Array.from(
      { length: scaleMax - scaleMin + 1 },
      (_, i) => scaleMin + i,
    );

    const handleScaleKeyDown = (
      event: React.KeyboardEvent<HTMLDivElement>,
      value: number,
    ) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        answerScale(value);
      }
    };

    return (
      <StyledQuestionContainer
        isMobile={isMobile()}
        id={`question-${question.id}`}
      >
        <StyledQuestionText>{questionText}</StyledQuestionText>
        <FlexContainer justify="space-between" gap={12} width="100%">
          {scaleRange.map((value) => (
            <StyledScale
              id={`scale-${value}`}
              key={value}
              tabIndex={0}
              role="button"
              aria-pressed={isSelectedScale(value) ? 'true' : 'false'}
              className={isSelectedScale(value) ? 'selected' : ''}
              onClick={() => answerScale(value)}
              onKeyDown={(e) => handleScaleKeyDown(e, value)}
            >
              {value}
            </StyledScale>
          ))}
        </FlexContainer>
        <StyledScaleLabels justify="space-between">
          <div id={`scale-min-label-${question.id}`}>{scaleMinLabel}</div>
          <div id={`scale-max-label-${question.id}`}>{scaleMaxLabel}</div>
        </StyledScaleLabels>
      </StyledQuestionContainer>
    );
  };

  return (
    <StyledContainer
      direction="column"
      flex="1"
      align="flex-start"
      justify="center"
      gap={16}
    >
      {/* Start survey view */}
      {viewMode === ViewMode.START && (
        <FlexContainer
          id="start-survey-view"
          direction="column"
          align="center"
          justify="center"
          className="w-full h-full start-survey-view"
        >
          <h3>{hardcodedTranslations.survey.introTitle[language]}</h3>
          <p
            className="m-0"
            dangerouslySetInnerHTML={{
              __html: hardcodedTranslations.survey.introDescription[language],
            }}
          />
          <FlexContainer
            justify="center"
            align="center"
            gap={16}
            className="mt-5"
          >
            <Dropdown
              id="language-dropdown"
              defaultValue={urlLanguage}
              options={sortAlphabeticallyWithDKFirst(
                survey?.supportedLanguages || [],
              )?.map((value) => ({
                label: languageDisplayLabel[value],
                value,
              }))}
              value={language}
              onChange={(e) => setLanguage(e.value)}
              className="white with-border"
            />
            <AppButton
              id="start-survey-button"
              label={hardcodedTranslations.survey.startNow[language]}
              icon="pi pi-arrow-right"
              iconPos="right"
              onClick={() => setViewMode(ViewMode.QUESTIONS)}
            />
          </FlexContainer>
        </FlexContainer>
      )}

      {/* Questions view */}
      {viewMode === ViewMode.QUESTIONS && currentQuestion && (
        <FlexContainer
          id="questions-survey-view"
          direction="column"
          align="center"
          justify="flex-start"
          className="w-full h-full questions-survey-view"
        >
          <StyledProgressHeader>
            <div className="survey-progress-text">
              {hardcodedTranslations.quiz.currentOutOfTotal[language](
                currentQuestionIndex + 1,
                questions.length,
              )}
            </div>
            <StyledProgressBar
              value={(100 * (currentQuestionIndex + 1)) / questions.length}
              showValue={false}
            />
          </StyledProgressHeader>

          {(currentQuestion.type === QuestionType.RATING_SCALE &&
            renderScaleQuestion(currentQuestion)) ||
            null}

          {currentQuestion.type === QuestionType.TEXT && (
            <StyledQuestionContainer isMobile={isMobile()}>
              <StyledQuestionText>
                {getTranslatedContent(currentQuestion, language, 'text')}
              </StyledQuestionText>
              <StyledTextarea
                id="feedback-textarea"
                className="w-full white"
                rows={5}
                value={String(
                  answers.find(
                    (answer) => answer.questionId === currentQuestion.id,
                  )?.answer || '',
                )}
                onChange={(e) => giveFeedback(e.target.value)}
              />
            </StyledQuestionContainer>
          )}

          <FlexContainer justify="space-between" className="mt-4 w-full">
            <AppButton
              id="back-survey-button"
              label={hardcodedTranslations.buttons.previous[language]}
              severity="secondary"
              type="outlined"
              onClick={() => {
                if (currentQuestionIndex > 0) {
                  setCurrentQuestionIndex(currentQuestionIndex - 1);
                } else {
                  handleBackToStart();
                }
              }}
            />
            {showForwardButton && (
              <AppButton
                id={
                  isLastQuestion ? 'finish-survey-button' : 'next-survey-button'
                }
                label={
                  isLastQuestion
                    ? hardcodedTranslations.buttons.finish[language]
                    : hardcodedTranslations.buttons.next[language]
                }
                onClick={handleNextQuestion}
              />
            )}
          </FlexContainer>
        </FlexContainer>
      )}

      {/* Completed view */}
      {(viewMode === ViewMode.COMPLETED ||
        viewMode === ViewMode.ALREADY_SUBMITTED) && (
        <FlexContainer
          id="completed-survey-view"
          direction="column"
          align="center"
          justify="center"
          className="w-full h-full completed-survey-view"
        >
          <img
            style={{ maxHeight: '295px' }}
            src={surveySubmittedImage}
            alt={hardcodedTranslations.survey.completedAlt[language]}
            className="mb-4"
          />
          <h3>
            {viewMode === ViewMode.COMPLETED
              ? hardcodedTranslations.survey.completedTitle[language]
              : hardcodedTranslations.survey.alreadySubmittedTitle[language]}
          </h3>
          <p className="m-0">
            {viewMode === ViewMode.COMPLETED
              ? hardcodedTranslations.survey.completedDescription[language]
              : hardcodedTranslations.survey.alreadySubmittedDescription[
                  language
                ]}
          </p>
        </FlexContainer>
      )}
    </StyledContainer>
  );
};
